import { currentYear } from "@/lib/siteYear";
// Subcategory landing config for /generators/category/:slug
// Each subcategory has SEO metadata, intro copy, an FAQ block, and a filter
// function that selects matching generators from the generators table.

export interface GeneratorRow {
  id: string;
  brand: string;
  model: string;
  slug: string;
  generator_type: string;
  fuel_type: string;
  rated_power_watts?: number | null;
  power_output_watts?: number | null;
  inverter?: boolean | null;
  wheels?: boolean | null;
  weight_kg?: number | null;
  description?: string | null;
}

export interface GeneratorSubcategoryConfig {
  slug: string;
  title: string;
  h1: string;
  metaTitle: string;
  metaDescription: string;
  intro: string;
  longCopy: string[];
  faqs: { q: string; a: string }[];
  filter: (g: GeneratorRow) => boolean;
}

const yr = currentYear();

export const generatorSubcategories: Record<string, GeneratorSubcategoryConfig> = {
  "inverter-generators": {
    slug: "inverter-generators",
    title: "Inverter Generators",
    h1: `Best Inverter Generators UK ${yr}`,
    metaTitle: `Best Inverter Generators UK ${yr} | Quiet, Clean Power Reviewed`,
    metaDescription:
      "Compare the best inverter generators in the UK. Quiet, fuel-efficient and safe for laptops, phones and sensitive electronics. Independent expert reviews.",
    intro:
      "Inverter generators produce clean, stable AC power that's safe for laptops, phones, TVs and other sensitive electronics. They're quieter and more fuel-efficient than traditional open-frame generators, making them ideal for camping, caravans, motorhomes and home backup.",
    longCopy: [
      "Unlike conventional generators that produce raw AC power, inverter generators convert AC to DC and back to a clean sine wave. The result is total harmonic distortion (THD) typically below 3% — well within safe limits for modern electronics.",
      "Most quality UK inverter generators run between 50–60 dB at quarter load, roughly the volume of a normal conversation. Look for eco-throttle or smart-throttle modes that adjust engine speed based on demand to save fuel and reduce noise.",
      "Parallel capability lets you link two inverter generators together to double your power output — useful for running an air conditioner or larger appliance at a campsite or worksite.",
      "Sizing is where most buyers go wrong. Work from the appliances you actually need rather than the size of the property: a fridge-freezer, boiler, router and lighting comes to roughly 1.5–2.5kW of continuous load. Then check starting watts — motors in fridges, pumps and boilers briefly draw two to three times their running figure, and an undersized generator will trip every time the compressor kicks in.",
      "Runtime figures are quoted at 25% or 50% load, not full output. A 2kW inverter running a fridge and a few lights will comfortably beat its headline hours; the same machine near its ceiling will not. For overnight backup, look for at least eight hours at half load and check the actual tank capacity rather than trusting the marketing figure.",
      "Dual fuel inverter generators run on petrol or LPG. Bottled gas stores indefinitely, which is a real advantage for a machine that may sit unused for two years between power cuts — petrol left in a tank goes stale within months and gums up the carburettor. If the generator is bought purely for emergencies, dual fuel is worth the premium.",
      "Never plug a generator into a wall socket to feed the house. Backfeeding is illegal in the UK and puts lethal voltage onto the network for anyone working on the line. Either run appliances directly from the generator on heavy-duty leads, or have a qualified electrician fit a manual changeover switch. Always run the generator outdoors, well away from doors, windows and air bricks — carbon monoxide from generators kills people in the UK every year.",
      "Maintenance is minimal but not optional. Change the oil after the first five hours and annually thereafter, keep the air filter clean, and either drain the fuel or add a stabiliser before winter storage. Run the machine under load for half an hour twice a year so you find out it works before you actually need it.",
    ],
    faqs: [
      {
        q: "Are inverter generators safe for laptops?",
        a: "Yes — inverter generators produce clean sine wave power with low total harmonic distortion (typically under 3%), making them safe for laptops, phones, TVs and other sensitive electronics.",
      },
      {
        q: "How loud is an inverter generator?",
        a: "Most UK inverter generators run between 50 and 60 decibels at quarter load — about the level of a quiet conversation. Open-frame conventional generators are typically 10–15 dB louder.",
      },
      {
        q: "What size inverter generator do I need?",
        a: "For camping or a small caravan, a 1,000–2,000W model is usually enough. For home backup running a fridge, lights and a kettle, look at 2,500–3,500W. Heavy-duty backup needs 4,000W or more.",
      },
      {
        q: "What is the difference between an inverter generator and a normal generator?",
        a: "A conventional generator produces raw AC power directly from the alternator and runs at a fixed engine speed regardless of load. An inverter generator converts that output to DC and back to a clean sine wave, and throttles the engine to match demand — so it is quieter, more fuel efficient and safe for sensitive electronics.",
      },
      {
        q: "Can an inverter generator power a house?",
        a: "It can power essential circuits, not a whole house. A 3–4kW inverter generator will cover a fridge-freezer, boiler, router, lighting and phone charging. High-draw items such as electric showers, immersion heaters and ovens need far more output. Connection must be through a changeover switch fitted by a qualified electrician.",
      },
      {
        q: "How long will an inverter generator run on a tank?",
        a: "Typically 4–12 hours depending on tank size and load. Manufacturer figures assume 25–50% load, so expect noticeably less at full output. Eco-throttle mode makes a substantial difference on light loads.",
      },
      {
        q: "Is a dual fuel inverter generator worth it?",
        a: "For emergency backup, yes. LPG stores indefinitely while petrol degrades within months, so a dual fuel unit is far more likely to start after two years in the garage. For regular use, petrol alone is cheaper and simpler.",
      },
      {
        q: "Can you run an inverter generator in the rain?",
        a: "Not unprotected. Generators must be run outdoors but kept dry — use a purpose-made generator tent or open-sided cover that allows airflow and exhaust clearance. Never run one in a garage, shed, porch or under a closed canopy.",
      },
      {
        q: "How much does it cost to run an inverter generator?",
        a: "A 2kW inverter running at half load burns roughly 0.5–0.8 litres of petrol an hour, so around £0.75–£1.20 per hour at current UK pump prices. Eco-throttle cuts that meaningfully on light loads.",
      },
    ],

    filter: g => g.generator_type === "inverter" || g.inverter === true,
  },

  "open-frame-generators": {
    slug: "open-frame-generators",
    title: "Open Framed Generators",
    h1: `Best Open Framed Generators UK ${yr}`,
    metaTitle: `Best Open Framed Generators UK ${yr} | Site & Backup Power`,

    metaDescription:
      "Open-frame framed generators offer maximum power-to-pound value for trade, building sites and home backup. Compare the best framed petrol & diesel models in the UK.",
    intro:
      "Open-frame (framed) generators are the workhorses of building sites, allotments and emergency home backup. They're heavier and louder than inverter models, but offer significantly more power for the money and are built to take a knock.",
    longCopy: [
      "Framed generators use a steel roll-cage frame that protects the engine and alternator from impact damage — perfect for vans, sites and rough handling. Most have pneumatic wheels and lifting eyes for moving around a job.",
      "Because they don't use inverter electronics, framed generators deliver high continuous power output efficiently. Expect 2,500W to 8,000W from typical UK petrol and diesel framed models.",
      "Framed generators are ideal for power tools, lighting, pumps, welders and most domestic appliances. They're not the best choice for sensitive electronics unless paired with a surge protector or AVR (automatic voltage regulator).",
    ],
    faqs: [
      {
        q: "What's the difference between framed and inverter generators?",
        a: "Framed (open-frame) generators are larger, louder and produce raw AC power suited to power tools, lighting and appliances. Inverter generators are quieter, more fuel-efficient and produce clean power safe for electronics — but cost more per watt.",
      },
      {
        q: "Are framed generators safe for fridges and freezers?",
        a: "Yes — modern framed generators with AVR (automatic voltage regulation) are fine for fridges, freezers, pumps and most household appliances. For laptops or TVs, choose an inverter model instead.",
      },
      {
        q: "How long do framed generators run on a tank?",
        a: "Typically 8–12 hours at half load on petrol, and 10–16 hours on diesel. Larger industrial framed diesel generators can run 24+ hours on a single tank.",
      },
    ],
    filter: g =>
      (g.generator_type === "portable" || g.generator_type === "industrial") &&
      g.inverter !== true &&
      (g.fuel_type === "petrol" || g.fuel_type === "diesel" || g.fuel_type === "dual_fuel"),
  },

  "portable-petrol-generators": {
    slug: "portable-petrol-generators",
    title: "Portable Petrol Generators",
    h1: `Best Portable Petrol Generators UK ${yr}`,
    metaTitle: `Best Portable Petrol Generators UK ${yr} | Camping & Backup`,
    metaDescription:
      "The best portable petrol generators in the UK for camping, caravans, allotments and emergency home backup. Independent reviews, prices and specs.",
    intro:
      "Portable petrol generators are the most popular choice for UK households, campers and tradespeople. Petrol is widely available, generators start easily in cold weather, and prices start under £200 for a basic unit.",
    longCopy: [
      "Portable petrol generators typically range from 800W up to 5,000W, weigh 15–60kg, and offer runtimes of 4–12 hours per tank depending on load. Most have a recoil pull-start; larger models add electric start.",
      "For camping and caravans, a 1,000–2,000W inverter petrol generator is the sweet spot — light enough to lift, quiet enough not to upset neighbours, and powerful enough to run a kettle, fridge and a few lights.",
      "For DIY or home backup, a 2,500–4,500W open-frame petrol model gives you headroom for power tools, a circular saw or a small immersion heater. Always run generators outdoors with at least 3 metres clear of windows or doors.",
    ],
    faqs: [
      {
        q: "How long does petrol last in a generator?",
        a: "Petrol degrades after 3–6 months in a generator tank. Use a fuel stabiliser or drain the tank if storing for longer. E10 petrol degrades faster than E5 super unleaded.",
      },
      {
        q: "Can I run a kettle off a portable petrol generator?",
        a: "Yes — a 2,000W kettle needs at least a 2,200W (running) generator. For inverter models, check the running watts, not just the peak/surge output.",
      },
      {
        q: "Is it safe to run a petrol generator in a garage?",
        a: "No — never run any petrol generator in a garage, shed or enclosed space, even with the door open. Carbon monoxide can build up rapidly and is fatal. Always run generators outdoors, well away from windows and doors.",
      },
    ],
    filter: g => g.generator_type === "portable" && g.fuel_type === "petrol",
  },

  "single-phase-generators": {
    slug: "single-phase-generators",
    title: "Single Phase Generators",
    h1: `Best Single Phase Generators UK ${yr}`,
    metaTitle: `Best Single Phase Generators UK ${yr} | 230V Domestic Power`,
    metaDescription:
      "Single phase 230V generators for homes, allotments, caravans and small trade jobs. Compare the best UK single phase petrol, diesel and dual fuel generators.",
    intro:
      "Single phase generators output 230V AC — the standard UK domestic supply. They power everything you'd find in a normal home: fridges, lighting, kettles, power tools, electric heaters and consumer electronics. If you don't have an industrial three-phase supply at home, you want a single-phase generator.",
    longCopy: [
      "Almost all generators sold for domestic, camping and light trade use in the UK are single phase. They typically deliver power through 13A 3-pin sockets and 16A 'Commando' (Ceeform) blue sockets.",
      "Single phase generators are available from 800W up to around 12,000W. Beyond that, you generally need a three-phase generator — though three-phase units can usually deliver single-phase output too.",
      "Choose your size by adding up the running watts of everything you want to run at once, then adding 20–30% headroom for motor start-up surges (fridges, pumps, compressors).",
    ],
    faqs: [
      {
        q: "Is my house single phase or three phase?",
        a: "Almost all UK homes are single phase 230V. Three-phase 400V supplies are usually only fitted to large rural properties, workshops, farms or commercial premises with high power demands.",
      },
      {
        q: "Can a single phase generator run a whole house?",
        a: "A 6,000–8,000W single phase generator can power most essentials — fridge, freezer, lighting, kettle, boiler controls and a few sockets — but not everything at once. For full whole-house backup, a 10,000W+ standby generator is usually needed.",
      },
      {
        q: "What sockets does a single phase generator have?",
        a: "Most UK single phase generators have 1–2 standard 13A 3-pin sockets and a 16A or 32A blue Commando socket. Larger models add a 32A Commando outlet for higher loads.",
      },
    ],
    filter: g =>
      // Single phase = pretty much everything except heavy industrial/three-phase units.
      // Use power threshold + non-battery as a sensible proxy.
      (g.fuel_type === "petrol" || g.fuel_type === "diesel" || g.fuel_type === "dual_fuel") &&
      (g.rated_power_watts ?? g.power_output_watts ?? 0) <= 12000,
  },

  "dual-fuel-and-lpg-generators": {
    slug: "dual-fuel-and-lpg-generators",
    title: "Dual Fuel and LPG Generators",
    h1: `Best Dual Fuel & LPG Generators UK ${yr}`,
    metaTitle: `Best Dual Fuel & LPG Generators UK ${yr} | Petrol + Gas Power`,
    metaDescription:
      "Dual fuel generators run on petrol or LPG gas, giving you fuel flexibility, longer storage life and cleaner emissions. Compare the best UK dual fuel models.",
    intro:
      "Dual fuel generators run on either petrol or LPG (liquefied petroleum gas), letting you switch fuel based on price, availability or how long you need to store the generator. LPG burns cleaner than petrol, doesn't degrade in storage, and produces less carbon monoxide.",
    longCopy: [
      "Switching between petrol and LPG is usually as simple as turning a fuel selector valve. Most dual fuel generators come with the LPG hose and regulator included, so you can connect to a standard 6kg or 13kg patio gas bottle.",
      "LPG is the better choice for emergency home backup — it stores indefinitely, so you can keep a couple of bottles ready for a power cut without worrying about stale fuel. Petrol is handier for short notice trips or jobs.",
      "Expect around 10–15% lower peak power output when running on LPG vs petrol, but lower exhaust emissions and a longer engine life thanks to cleaner combustion.",
    ],
    faqs: [
      {
        q: "How long does a 13kg LPG bottle last in a dual fuel generator?",
        a: "Roughly 8–12 hours at half load on a 3,000W dual fuel generator. Larger 5,000W+ models will get through a 13kg bottle in 5–8 hours of continuous use.",
      },
      {
        q: "Is LPG cheaper than petrol for generators?",
        a: "LPG is usually 10–25% cheaper per kWh than petrol in the UK, especially if you're using your own bottles rather than forecourt cylinders. The bigger saving is storage — LPG doesn't go off.",
      },
      {
        q: "Can dual fuel generators run on natural gas?",
        a: "Most off-the-shelf dual fuel generators are designed for bottled LPG, not mains natural gas. Some industrial models support natural gas with a conversion kit — check the spec sheet.",
      },
    ],
    filter: g => g.fuel_type === "dual_fuel",
  },

  "heavy-duty-diesel-generators": {
    slug: "heavy-duty-diesel-generators",
    title: "Heavy Duty Diesel Generators",
    h1: `Best Heavy Duty Diesel Generators UK ${yr}`,
    metaTitle: `Best Heavy Duty Diesel Generators UK ${yr} | Site & Standby Power`,
    metaDescription:
      "Heavy duty diesel generators for construction sites, farms, workshops and whole-house standby. Compare the best UK industrial and standby diesel models.",
    intro:
      "Heavy duty diesel generators deliver high continuous power output, long runtimes and the kind of mechanical durability you need for construction sites, farms, workshops and whole-house backup. Diesel engines run cooler, last longer and use 30% less fuel per kWh than equivalent petrol units.",
    longCopy: [
      "Industrial diesel generators typically deliver 5kW to 50kW+ of continuous power, with tank capacities of 25–200 litres for runtimes well over 24 hours. Many include AVR, low-oil shutdown, and weatherproof canopies for permanent outdoor installation.",
      "Standby diesel generators are designed to start automatically when mains power fails, restoring power to a property in 5–15 seconds. They're usually paired with an automatic transfer switch (ATS) wired into the consumer unit.",
      "Diesel fuel keeps better than petrol — typically 12 months in a sealed tank with a stabiliser — making diesel the natural choice for emergency backup that might sit unused for months at a time.",
    ],
    faqs: [
      {
        q: "How long can a heavy duty diesel generator run continuously?",
        a: "Most industrial diesel generators are rated for 24/7 continuous duty, with oil and filter service intervals of 200–500 hours. Standby units are typically rated for 200–500 hours per year.",
      },
      {
        q: "Do I need planning permission for a standby diesel generator?",
        a: "Generally no for a domestic backup generator, but you should check local noise limits and Building Regulations for fuel storage. Larger commercial installations may need planning consent and an environmental permit.",
      },
      {
        q: "Are diesel generators more reliable than petrol?",
        a: "Yes — diesel engines have fewer ignition components, run cooler and typically last 2–3 times longer than petrol equivalents. They cost more upfront but lower fuel and maintenance costs even out over time.",
      },
    ],
    filter: g =>
      g.fuel_type === "diesel" &&
      (g.generator_type === "industrial" ||
        g.generator_type === "standby" ||
        (g.rated_power_watts ?? g.power_output_watts ?? 0) >= 5000),
  },

  "three-phase-generators": {
    slug: "three-phase-generators",
    title: "Three Phase Generators",
    h1: `Best Three Phase Generators UK ${yr}`,
    metaTitle: `Three Phase Generators UK ${yr} | 400V Industrial Power`,
    metaDescription:
      "Three phase 400V generators for industrial sites, workshops, farms and large machinery. Compare the best UK three phase diesel and petrol generators.",
    intro:
      "Three phase generators output 400V AC across three live phases, the standard supply for industrial machinery, large motors, CNC equipment and big workshops. Most also deliver 230V single phase output for ancillary loads.",
    longCopy: [
      "Three phase power delivers more efficient energy transfer than single phase — three-phase motors are smaller, cheaper and run cooler than single-phase motors of the same power. That's why most industrial machinery is three phase.",
      "Look for three phase generators with 32A and 63A red Commando (CEEform) sockets, plus 13A and 16A blue sockets for single-phase loads. AVR is essential to protect motors and electronics.",
      "Sizing a three phase generator: total your three phase load in kW, add headroom for motor starting (typically 3× the running kW), and pick a generator with a continuous (PRP) rating to match.",
    ],
    faqs: [
      {
        q: "Do I need a three phase generator?",
        a: "Only if you have three-phase machinery — large motors, industrial pumps, CNC machines, welders rated above 9kW, or commercial cooking equipment. For domestic and most light trade use, single phase is fine.",
      },
      {
        q: "Can a three phase generator power single phase appliances?",
        a: "Yes — virtually all three phase generators provide 230V single phase outlets alongside the 400V three phase output. You can run normal household appliances from the single phase sockets.",
      },
      {
        q: "What size three phase generator do I need for a workshop?",
        a: "Add up the kW rating of every machine you might run simultaneously, multiply motor loads by 3 for start-up surge, then pick the next size up. Typical small workshops need 10–20 kVA; larger industrial sites need 30–100 kVA+.",
      },
    ],
    filter: g =>
      g.fuel_type === "diesel" &&
      g.generator_type === "industrial" &&
      (g.rated_power_watts ?? g.power_output_watts ?? 0) >= 8000,
  },

  "battery-power-packs": {
    slug: "battery-power-packs",
    title: "Battery Power Packs",
    h1: `Best Battery Power Stations UK ${yr}`,
    metaTitle: `Best Battery Power Packs & Solar Generators UK ${yr}`,
    metaDescription:
      "Silent, zero-emission battery power packs and solar generators for camping, off-grid living and home backup. Compare the best UK portable power stations.",
    intro:
      "Battery power packs (also called portable power stations or solar generators) store electricity in a lithium battery and deliver it through 230V AC sockets, USB-C, USB-A and 12V outlets. They're silent, produce no fumes and can be used safely indoors — perfect for tents, campervans, home offices and emergency backup.",
    longCopy: [
      "Capacity is measured in watt-hours (Wh): a 1,000Wh power pack will run a 100W laptop for around 10 hours, or a 50W mini fridge overnight. Output is measured in watts (W): a 1,000W AC output handles most appliances, but high-draw items like kettles or hairdryers need 1,500W+.",
      "Most modern UK portable power stations use lithium iron phosphate (LiFePO4) cells, which last 3,000+ charge cycles — about 10 years of weekly use. Older lithium-ion (NMC) cells last 500–1,000 cycles.",
      "Pair a battery power pack with a folding solar panel for true off-grid running. A 200W panel will fully recharge a 1,000Wh power station in 5–7 hours of summer sun.",
    ],
    faqs: [
      {
        q: "Can I use a battery power pack indoors?",
        a: "Yes — battery power packs produce no exhaust fumes and are completely safe indoors, unlike petrol or diesel generators. They're a popular choice for power cuts, working from home and small flats.",
      },
      {
        q: "How long do battery power packs last?",
        a: "Modern LiFePO4 power stations are rated for 3,000–6,500 charge cycles to 80% capacity — typically 10+ years of normal use. Cheaper lithium-ion (NMC) models last 500–1,000 cycles.",
      },
      {
        q: "Can a battery power pack run a fridge during a power cut?",
        a: "Yes — a 1,000Wh power station will run a typical A-rated fridge for 12–18 hours. A 2,000Wh model gives you 24–36 hours, plenty for most short UK power cuts.",
      },
    ],
    filter: g => g.fuel_type === "battery" || g.generator_type === "solar",
  },
};

export const generatorSubcategoryList = Object.values(generatorSubcategories);
